import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { Loader } from 'web-components';

import { matchPath, useHistory } from 'react-router-dom';
import { irisEnabled } from './auth/iris';
import { externalAuthPath } from './attrs/routes';
import customTheme from './attrs/theme';
import { StyledSnackbarProvider } from './elements';
import LoggedIn from './LoggedIn';
import LoggedOut from './LoggedOut';
import Notifications from './components/Notifications';
import Confirmations from './components/Confirmations';
import ExternalAuth from './containers/Auth/ExternalLogin';
import { checkAuthentication } from './redux/auth/actions';
import { getIsAuthenticated } from './redux/auth/selectors';
import FeatureToggles from './components/FeatureToggles';
import { CacheBuster } from './CacheBuster';
import { subscribeToAccessDenials } from './api/accessDeniedSession';

// to fix CORS issues
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import '@fontsource/material-icons';

function App() {
  const { location } = useHistory();
  const dispatch = useDispatch();
  const store = useStore();
  const [isLoading, setIsLoading] = useState(true);
  const [prevPath, setPrevPath] = useState(undefined);
  const isAuthenticated = useSelector(getIsAuthenticated);
  const isExternalAuthPath = Boolean(matchPath(location.pathname, { path: externalAuthPath, exact: true }));
  const isAuthPath =
    isExternalAuthPath ||
    ['/login', '/register', '/forgot-password', '/set-password'].some(path => location.pathname.startsWith(path));

  useEffect(() => subscribeToAccessDenials(store), [store]);

  useEffect(() => {
    dispatch(checkAuthentication());
    const manualLogout = sessionStorage.getItem('manual_logout');
    if (!manualLogout) {
      setPrevPath(sessionStorage.getItem('redirect_after_login') || '/dashboard');
    }
    sessionStorage.removeItem('manual_logout');
    sessionStorage.removeItem('redirect_after_login');
    setIsLoading(false);
  }, [dispatch]);

  useEffect(() => {
    if (location.pathname === '/' || isAuthPath) {
      return;
    }

    setPrevPath(location);
  }, [isAuthPath, location]);

  let authContent;

  if (isExternalAuthPath && !irisEnabled) {
    authContent = <ExternalAuth />;
  } else if (isAuthenticated) {
    authContent = <LoggedIn prevPath={prevPath} />;
  } else {
    authContent = <LoggedOut prevPath={prevPath} />;
  }

  return (
    <CacheBuster>
      {({ loading, isLatestVersion, refreshCacheAndReload }) => {
        if (loading) return null;
        if (!loading && !isLatestVersion) {
          refreshCacheAndReload();
        }

        return (
          <Suspense fallback={<Loader standalone />}>
            <ThemeProvider theme={customTheme}>
              <FeatureToggles />
              <CssBaseline />
              {isLoading ? (
                <Loader standalone />
              ) : (
                <StyledSnackbarProvider dense maxSnack={1}>
                  {authContent}
                  <Notifications />
                  <Confirmations />
                </StyledSnackbarProvider>
              )}
            </ThemeProvider>
          </Suspense>
        );
      }}
    </CacheBuster>
  );
}

export default App;
